import { yupResolver } from '@hookform/resolvers/yup';
import { Alert, Box, Button } from '@mui/material';
import React, { FC, useCallback, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import {
    ControlledDatePicker,
    ControlledInput,
    formatDateString,
    FormCard,
    FormGrid,
    isAxiosError,
    isConflictException,
    mapDateToApiDate,
    mapDateToRequiredApiDate,
    Page,
    Section,
} from '../../../shared';
import { ClosedDaysWarningDialog } from '../../components';
import { useCreateClosedDays } from '../../hooks';
import { IClosedDayPlanningEntry, IClosedDaysForm, IClosedDayShipment, ISaveClosedDays } from '../../models';
import { useClosedDaysSchema } from '../../validators';

export const ClosedDaysEditPage: FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { mutateAsync: createClosedDays, isPending } = useCreateClosedDays();
    const [alreadyClosedDates, setAlreadyClosedDates] = useState<string[]>([]);
    const [shipments, setShipments] = useState<IClosedDayShipment[]>([]);
    const [planningEntriesToConfirm, setPlanningEntriesToConfirm] = useState<{
        closedDays: ISaveClosedDays;
        planningEntries: IClosedDayPlanningEntry[];
    }>();

    const form = useForm<IClosedDaysForm>({
        resolver: yupResolver(useClosedDaysSchema()),
        mode: 'all',
    });

    const saveClosedDays = useCallback(
        async (closedDays: ISaveClosedDays) => {
            setAlreadyClosedDates([]);
            setShipments([]);
            try {
                await createClosedDays(closedDays);
                navigate('/admin/closed-days');
            } catch (e) {
                if (isConflictException(e) && isAxiosError(e)) {
                    const {
                        alreadyClosed = [],
                        shipments = [],
                        planningEntries = [],
                    } = (e.response?.data || {}) as {
                        alreadyClosed?: string[];
                        shipments?: IClosedDayShipment[];
                        planningEntries?: IClosedDayPlanningEntry[];
                    };
                    setAlreadyClosedDates(alreadyClosed);
                    setShipments(shipments);
                    if (planningEntries.length) {
                        setPlanningEntriesToConfirm({ closedDays, planningEntries });
                    }
                }
            }
        },
        [createClosedDays, navigate],
    );

    const onSubmit = useCallback(
        ({ startDate, endDate, description }: IClosedDaysForm) =>
            saveClosedDays({
                startDate: mapDateToRequiredApiDate(startDate),
                endDate: mapDateToApiDate(endDate),
                description,
            }),
        [saveClosedDays],
    );

    const onConfirmDeletePlanningEntries = useCallback(async () => {
        if (planningEntriesToConfirm) {
            const { closedDays } = planningEntriesToConfirm;
            setPlanningEntriesToConfirm(undefined);
            await saveClosedDays({ ...closedDays, confirmDeletePlanningEntries: true });
        }
    }, [planningEntriesToConfirm, saveClosedDays]);

    const actions = useMemo(
        () => [
            <Button variant="contained" color="primary" onClick={form.handleSubmit(onSubmit)} disabled={isPending}>
                {t('save')}
            </Button>,
            <Button onClick={() => navigate('/admin/closed-days')} color="secondary">
                {t('cancel')}
            </Button>,
        ],
        [form, onSubmit, isPending, t, navigate],
    );

    const reversedActions = useMemo(() => [...actions].reverse(), [actions]);

    return (
        <Page title={t('newClosedDays')} actions={reversedActions} onBack={() => navigate(-1)} loading={isPending}>
            <FormProvider {...form}>
                <FormCard handleSubmit={form.handleSubmit(onSubmit)} actions={actions}>
                    <Section title={t('data')}>
                        <FormGrid containerProps={{ direction: 'column' }}>
                            <FormGrid>
                                <ControlledDatePicker name="startDate" label={t('from')} required width={400} />
                                <ControlledDatePicker name="endDate" label={t('untilAndIncluding')} width={400} />
                            </FormGrid>
                            <ControlledInput name="description" label={t('description')} sx={{ minWidth: '400px' }} />

                            {alreadyClosedDates.length > 0 && (
                                <Alert severity="error">
                                    {t('daysAlreadyClosed')}
                                    <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                                        {alreadyClosedDates.map((date) => (
                                            <li key={date}>{formatDateString(date)}</li>
                                        ))}
                                    </Box>
                                </Alert>
                            )}

                            {shipments.length > 0 && (
                                <Alert severity="error">
                                    {t('closedDaysHaveShipments')}
                                    <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                                        {shipments.map(({ id, shipmentDate, receiver }) => (
                                            <li key={id}>
                                                <Link to={`/planning/shipments/${id}/detail`}>
                                                    {formatDateString(shipmentDate)} - {receiver.name}
                                                </Link>
                                            </li>
                                        ))}
                                    </Box>
                                </Alert>
                            )}
                        </FormGrid>
                    </Section>
                </FormCard>
            </FormProvider>

            <ClosedDaysWarningDialog
                open={!!planningEntriesToConfirm}
                title={t('closedDaysHavePlanningEntriesTitle')}
                text={t('closedDaysHavePlanningEntriesText')}
                items={(planningEntriesToConfirm?.planningEntries || []).map(
                    ({ date, organisation }) => `${formatDateString(date)} - ${organisation.name}`,
                )}
                confirmText={t('closedDaysHavePlanningEntriesConfirm')}
                onConfirm={onConfirmDeletePlanningEntries}
                onClose={() => setPlanningEntriesToConfirm(undefined)}
            />
        </Page>
    );
};
